/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { getTelemedicineConfig, getMyLimits, listDoctors, listMyAppointments } from '@/lib/telemedicine';
import PatientMarketplace from '@/pages/telemedicine/PatientMarketplace';
import DoctorCommandCenter from '@/pages/telemedicine/DoctorCommandCenter';
import { Skeleton } from '@/components/ui/skeleton';
import RedeemInviteBanner from '@/components/RedeemInviteBanner';
import OrientationHeroCard from '@/components/growth/OrientationHeroCard';
import IseeBundlePanel from '@/components/growth/IseeBundlePanel';
import FamilyMembersDashboard from '@/components/growth/FamilyMembersDashboard';
import PatientNextVisitHero from '@/components/telemedicine/PatientNextVisitHero';

const TelemedicinePage = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [limits, setLimits] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  const isDoctor = user?.category === 'medici';

  const refresh = useCallback(async () => {
    try {
      if (isDoctor) {
        // Doctor command center pulls its own data. We still need config for header.
        const cfg = await getTelemedicineConfig();
        setConfig(cfg);
      } else {
        const [cfg, lim, docs, appts] = await Promise.all([
          getTelemedicineConfig(),
          getMyLimits(),
          listDoctors(true),
          listMyAppointments(),
        ]);
        setConfig(cfg);
        setLimits(lim);
        setDoctors(docs.doctors || []);
        setAppointments(appts.appointments || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [isDoctor]);
  useEffect(() => { refresh(); }, [refresh]);

  if (loading && !isDoctor) {
    return (
      <div className="space-y-5" data-testid="telemedicine-page">
        <Skeleton className="h-48 rounded-3xl" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <Skeleton key={`tm-skel-${i}`} className="h-52 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="telemedicine-page" className="space-y-4">
      <RedeemInviteBanner />
      {!isDoctor && <PatientNextVisitHero />}
      {!isDoctor && <FamilyMembersDashboard />}
      {!isDoctor && <OrientationHeroCard />}
      {!isDoctor && <IseeBundlePanel isPatient={!isDoctor} />}
      {isDoctor
        ? <DoctorCommandCenter />
        : <PatientMarketplace config={config} limits={limits} doctors={doctors} appointments={appointments} loading={loading} onRefresh={refresh} />}
    </div>
  );
};

export default TelemedicinePage;
